import { useEffect, useMemo } from "react";
import { TFunction } from "i18next/typescript/t";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { App } from "antd";
import { UploadFile } from "antd/es/upload/interface";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import {
  useLazyGetCountriesQuery,
  useUpdateOrganizationByIdMutation,
  useLazyGetLanguagesQuery,
  useLazyGetAchievementsQuery,
  useLazyGetCommunityRolesQuery,
} from "@/redux/service";
import { UpdateOrganizationFormValues } from "@/features/organizations/list/types";
import {
  HookFormBaseDropdown,
  HookFormBaseUpload,
  HookFormInfiniteScrollDropdown,
  HookFormInput,
  HookFormRichTextInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  Chapters,
  MAX_PROFILE_PICTURE_FILE_SIZE,
  OrganizationCommunicationStatuses,
  Roles,
  SUPPORTED_PROFILE_PICTURE_FILE_FORMATS,
} from "@/shared/utils/constants";
import {
  CountryResponse,
  CountryQueryParams,
  LanguageQueryParams,
  LanguageResponse,
  CommunityRoleResponse,
  AchievementResponse,
  AchievementQueryParams,
  CommunityRoleQueryParams,
} from "@/redux/common/types";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { OrganizationDetailsFormProps } from "@/features/organizations/profile/types";
import { handleErrors } from "@/shared/utils/forms";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseModal from "@/components_v2/Modal/BaseModal";
import { hasExternalId } from "@/redux/organizations/typeGuards";
import { FullUserResponse } from "@/redux/users/types";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import { selectCurrentUser } from "@/redux/authentication/selectors";

const updateOrganizationSchema = (
  currentUser: FullUserResponse,
  t: TFunction,
): yup.ObjectSchema<UpdateOrganizationFormValues> =>
  yup.object().shape({
    name: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .when([], {
        is: () => currentUser.role === Roles.ADMIN.value,
        then: (schema) => schema.required(t("form.validation.required")),
        otherwise: (schema) => schema.optional(),
      }),
    parentOrganizationName: yup
      .string()
      .typeError(t("form.validation.notValid")),
    email: yup
      .string()
      .email(t("form.validation.notValid"))
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    address: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    externalId: yup
      .number()
      .integer(t("form.validation.notValid"))
      .typeError(t("form.validation.notValid"))
      .when([], {
        is: () => currentUser.role === Roles.ADMIN.value,
        then: (schema) => schema.required(t("form.validation.required")),
        otherwise: (schema) => schema.optional(),
      }),
    communicatingStatus: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .when([], {
        is: () => currentUser.role === Roles.ADMIN.value,
        then: (schema) => schema.required(t("form.validation.required")),
        otherwise: (schema) => schema.optional(),
      }),
    phoneNumber: yup.string().typeError(t("form.validation.notValid")),
    country: yup
      .number()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    language: yup
      .mixed<number | string>()
      .typeError(t("form.validation.notValid"))
      .nullable(),
    aboutUs: yup.string().typeError(t("form.validation.notValid")),
    website: yup
      .string()
      .url(t("form.validation.notValid"))
      .typeError(t("form.validation.notValid")),
    logo: yup
      .mixed<UploadFile>()
      .test("fileType", t("form.validation.notValid"), (value?: UploadFile) => {
        if (!value || !value.type) {
          return true;
        }
        return SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.some(
          (format) => format.contentType === value.type,
        );
      })
      .test(
        "fileSize",
        t("form.validation.maxFileSizeValidation", {
          fileSize: MAX_PROFILE_PICTURE_FILE_SIZE / (1024 * 1024),
        }),
        (value?: UploadFile) => {
          if (!value || value.size == null) {
            return true;
          }
          return value.size <= MAX_PROFILE_PICTURE_FILE_SIZE;
        },
      )
      .nullable(),
    chapter: yup.string().typeError(t("form.validation.notValid")),
    coverImage: yup
      .mixed<UploadFile>()
      .test("fileType", t("form.validation.notValid"), (value?: UploadFile) => {
        if (!value || !value.type) {
          return true;
        }
        return SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.some(
          (format) => format.contentType === value.type,
        );
      })
      .test(
        "fileSize",
        t("form.validation.maxFileSizeValidation", {
          fileSize: MAX_PROFILE_PICTURE_FILE_SIZE / (1024 * 1024),
        }),
        (value?: UploadFile) => {
          if (!value || value.size == null) {
            return true;
          }
          return value.size <= MAX_PROFILE_PICTURE_FILE_SIZE;
        },
      )
      .nullable(),
    achievements: yup
      .array(yup.number().typeError(t("form.validation.notValid")))
      .nullable(),
    communityRoles: yup
      .array(yup.number().typeError(t("form.validation.notValid")))
      .nullable(),
  });

const OrganizationProfileDetailsForm = ({
  organization,
  open,
  setOpen,
}: OrganizationDetailsFormProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const currentUser = useSelector(selectCurrentUser);
  const [updateOrganizationById] = useUpdateOrganizationByIdMutation();
  const [getCountries] = useLazyGetCountriesQuery();
  const [getAchievements] = useLazyGetAchievementsQuery();
  const [getCommunityRoles] = useLazyGetCommunityRolesQuery();
  const [getLanguages] = useLazyGetLanguagesQuery();

  const form = useForm<UpdateOrganizationFormValues>({
    resolver: (values, context, options) => {
      if (!currentUser) {
        return Promise.resolve({
          values: {},
          errors: {
            root: {
              type: "manual",
              message: "Loading user data...",
            },
          },
        });
      }
      return yupResolver(updateOrganizationSchema(currentUser, t))(
        values,
        context,
        options,
      );
    },
    mode: "onChange",
    defaultValues: {
      name: "",
      parentOrganizationName: "",
      email: "",
      address: "",
      phoneNumber: undefined,
      country: undefined,
      language: "",
      website: undefined,
      aboutUs: undefined,
      logo: null,
      coverImage: null,
      chapter: undefined,
      externalId: undefined,
      communicatingStatus: undefined,
      achievements: [],
      communityRoles: [],
    },
  });

  const formValues = form.watch();

  const currentCoverImageUrl = useMemo(() => {
    const uploadedCoverImage = formValues.coverImage;

    if (uploadedCoverImage && uploadedCoverImage instanceof File) {
      return URL.createObjectURL(uploadedCoverImage);
    }

    // If form value is explicitly null, return null (image was removed)
    if (uploadedCoverImage === null) {
      return null;
    }

    return organization.coverImage || null;
  }, [formValues.coverImage, organization.coverImage]);

  const currentOrganizationLogo = useMemo(() => {
    const uploadedLogo = formValues.logo;

    if (uploadedLogo && uploadedLogo instanceof File) {
      return URL.createObjectURL(uploadedLogo);
    }

    // If form value is explicitly null, return null (image was removed)
    if (uploadedLogo === null) {
      return null;
    }

    return organization.logo || null;
  }, [formValues.logo, organization.logo]);

  useEffect(() => {
    if (open && organization) {
      form.reset(
        {
          name: organization.name,
          parentOrganizationName: organization.parentOrganizationName,
          email: organization.email,
          address: organization.address,
          externalId: hasExternalId(organization)
            ? organization.externalId
            : undefined,
          communicatingStatus: organization.communicatingStatus,
          phoneNumber: organization.phoneNumber,
          country: organization.country,
          language: organization.language,
          aboutUs: organization.aboutUs,
          website: organization.website,
          chapter: organization.chapter,
          logo: organization.logo || null,
          coverImage: organization.coverImage || null,
          communityRoles: organization.communityRoles,
          achievements: organization.achievements,
        },
        { keepDefaultValues: true },
      );
    }
  }, [open, organization]);

  const fields = useMemo(
    () => [
      {
        label: t("organizations.labels.name"),
        field: (
          <HookFormInput
            name="name"
            required
            disabled={currentUser?.role !== Roles.ADMIN.value}
            placeholder={t("organizations.placeholders.enterName")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.parentOrganization"),
        field: (
          <HookFormInput
            name="parentOrganizationName"
            placeholder={t(
              "organizations.placeholders.enterParentOrganizationName",
            )}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.email"),
        required: true,
        field: (
          <HookFormInput
            name="email"
            required
            placeholder={t("organizations.placeholders.enterEmail")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.address"),
        required: true,
        field: (
          <HookFormInput
            name="address"
            required
            placeholder={t("organizations.placeholders.enterAddress")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.country"),
        required: true,
        field: (
          <HookFormInfiniteScrollDropdown<CountryResponse, CountryQueryParams>
            name="country"
            control={form.control}
            fetchFn={getCountries}
            labelFieldName="name"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="name"
            placeholder={t("organizations.placeholders.selectCountry")}
          />
        ),
      },
      {
        label: t("organizations.labels.phoneNumber"),
        required: false,
        field: (
          <HookFormInput
            name="phoneNumber"
            placeholder={t("organizations.placeholders.enterPhoneNumber")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.language"),
        required: false,
        field: (
          <HookFormInfiniteScrollDropdown<LanguageResponse, LanguageQueryParams>
            name="language"
            control={form.control}
            fetchFn={getLanguages}
            labelFieldName="name"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="name"
            placeholder={t("organizations.placeholders.selectLanguage")}
            allowClear
          />
        ),
      },
      ...(currentUser?.role === Roles.ADMIN.value
        ? [
            {
              label: t("organizations.labels.communicatingStatus"),
              required: false,
              field: (
                <HookFormBaseDropdown
                  name="communicatingStatus"
                  options={OrganizationCommunicationStatuses.asList()}
                  placeholder={t(
                    "organizations.placeholders.selectCommunicatingStatus",
                  )}
                  control={form.control}
                />
              ),
            },
            {
              label: t("organizations.labels.chapter"),
              required: false,
              field: (
                <HookFormBaseDropdown
                  name="chapter"
                  placeholder={t("organizations.placeholders.selectChapter")}
                  options={Chapters.asList()}
                  control={form.control}
                />
              ),
            },
            {
              label: t("organizations.labels.achievements"),
              required: false,
              field: (
                <HookFormInfiniteScrollDropdown<
                  AchievementResponse,
                  AchievementQueryParams
                >
                  name="achievements"
                  fetchFn={getAchievements}
                  labelFieldName="label"
                  valueFieldName="id"
                  lookupFieldName="ids"
                  searchFieldName="label"
                  multiple
                  placeholder={t(
                    "organizations.placeholders.selectAchievements",
                  )}
                  control={form.control}
                  defaultFilters={{
                    type: "organization",
                  }}
                />
              ),
            },
            {
              label: t("organizations.labels.communityRoles"),
              required: false,
              field: (
                <HookFormInfiniteScrollDropdown<
                  CommunityRoleResponse,
                  CommunityRoleQueryParams
                >
                  name="communityRoles"
                  fetchFn={getCommunityRoles}
                  labelFieldName="label"
                  valueFieldName="id"
                  lookupFieldName="ids"
                  searchFieldName="label"
                  multiple
                  placeholder={t(
                    "organizations.placeholders.selectCommunityRoles",
                  )}
                  control={form.control}
                  defaultFilters={{
                    type: "organization",
                  }}
                />
              ),
            },
            {
              label: t("organizations.labels.externalId"),
              required: false,
              field: (
                <HookFormInput
                  name="externalId"
                  type="number"
                  placeholder={t("organizations.placeholders.enterExternalId")}
                  control={form.control}
                />
              ),
            },
          ]
        : []),
      {
        label: t("organizations.labels.website"),
        required: false,
        field: (
          <HookFormInput
            name="website"
            placeholder={t("organizations.placeholders.enterWebsite")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.about"),
        required: false,
        field: <HookFormRichTextInput name="aboutUs" control={form.control} />,
      },
    ],
    [currentUser],
  );

  const handleSubmit = (values: UpdateOrganizationFormValues) => {
    const processedValues = { ...values };

    const hasNewLogo = values.logo && values.logo instanceof File;
    const hasNewCoverImage =
      values.coverImage && values.coverImage instanceof File;
    const logoRemoved = values.logo === null;
    const coverImageRemoved = values.coverImage === null;

    if (!hasNewLogo && !logoRemoved) {
      delete processedValues.logo;
    }
    if (!hasNewCoverImage && !coverImageRemoved) {
      delete processedValues.coverImage;
    }
    // If removed, send empty string (convertToFormData skips null values)
    if (logoRemoved) {
      processedValues.logo = "";
    }
    if (coverImageRemoved) {
      processedValues.coverImage = "";
    }

    updateOrganizationById({ id: organization.id, data: processedValues })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.organizationUpdated"),
        });
        setOpen(false);
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  const handleCancel = () => {
    form.reset();
    setOpen(false);
  };

  const handleRemoveCoverImage = () => {
    form.setValue("coverImage", null);
  };

  const handleRemoveLogo = () => {
    form.setValue("logo", null);
  };

  return (
    <BaseModal
      open={open}
      onCancel={handleCancel}
      modalPadding={false}
      className="organization-details-form-modal"
      width={800}
      footer={
        <div className="modal-footer-container modal-footer-buttons">
          <BaseButton key="cancel" variant="secondary" onClick={handleCancel}>
            {t("buttons.cancel")}
          </BaseButton>
          <BaseButton
            key="submit"
            variant="primary"
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("buttons.save")}
          </BaseButton>
        </div>
      }
    >
      <div>
        <div className="flex flex-col gap-200">
          <div
            className="organization-details-form-modal-cover-image-container full-width"
            style={{
              backgroundImage: currentCoverImageUrl
                ? `url(${currentCoverImageUrl})`
                : "none",
            }}
          >
            <div className="organization-details-form-modal-cover-image-overlay" />
            <HookFormBaseUpload
              name="coverImage"
              control={form.control}
              shouldShowValues={false}
              accept={SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.map(
                (format) => format.contentType,
              ).join(",")}
              maxFileSize={MAX_PROFILE_PICTURE_FILE_SIZE}
              className="organization-cover-image full-width full-height"
            />

            {currentCoverImageUrl && (
              <BaseButton
                variant="danger"
                size="small"
                leadingIcon={
                  <BaseSvgRenderer
                    component={DeleteIcon}
                    width="20"
                    height="20"
                    stroke="var(--base-white)"
                  />
                }
                onClick={handleRemoveCoverImage}
                className="cover-image-remove-btn"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-200 items-start">
          <div
            className={`logo-upload-circle organization-upload-logo ${currentOrganizationLogo && "hide-overlay-elements"}`}
            style={{
              backgroundImage: currentOrganizationLogo
                ? `url(${currentOrganizationLogo})`
                : "none",
            }}
          >
            <HookFormBaseUpload
              name="logo"
              control={form.control}
              shouldShowValues={false}
              accept={SUPPORTED_PROFILE_PICTURE_FILE_FORMATS.map(
                (format) => format.contentType,
              ).join(",")}
              maxFileSize={MAX_PROFILE_PICTURE_FILE_SIZE}
            />

            {currentOrganizationLogo && (
              <BaseButton
                variant="danger"
                size="small"
                leadingIcon={
                  <BaseSvgRenderer
                    component={DeleteIcon}
                    width="20"
                    height="20"
                    stroke="var(--base-white)"
                  />
                }
                onClick={handleRemoveLogo}
                className="logo-remove-btn"
              />
            )}
          </div>
        </div>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="organization-details-form-modal-form-fields flex flex-col gap-300">
          {fields.map((field) => (
            <FormFieldWrapper
              key={field.label}
              label={field.label}
              required={field.required}
            >
              {field.field}
            </FormFieldWrapper>
          ))}
        </div>
      </form>
    </BaseModal>
  );
};

export default OrganizationProfileDetailsForm;
